import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { ConfigurationService } from 'src/app/private/services/configuration.service';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import { Client } from '../events/models/client.model';
import { AppDialogService } from '../layout/dialog/app-dialog.service';
import { ApiResponse } from '../models/api.model';
import { AuthStore } from '../../core/stores/auth.store';
import { CreateComponent } from './modals/create/create.component';
import { DeleteComponent } from './modals/delete/delete.component';
import { EditComponent } from './modals/edit/edit.component';
import { ManagePrivateSellingComponent } from './modals/manage-private-selling/manage-private-selling.component';
import { GroupLink } from './models/group-links.model';
import { PrivateSellingWhitelistItem } from './models/private-selling.model';
import { GroupLinkService } from './services/group-links.service';
import { PrivateSellingService } from './services/private-selling.service';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import { buildGroupLinkColumns } from './group-links.columns';

type ResolverData = {
  groupLinksList: ApiResponse<GroupLink[]>;
  clientData: ApiResponse<Client>;
};

@Component({
  selector: 'app-group-links',
  templateUrl: './group-links.component.html',
  styleUrls: ['./group-links.component.css'],
  imports: [ButtonComponent, GsTableComponent, PaginationComponent],
})
export class GroupLinksComponent implements OnInit, OnDestroy {
  private dialogService = inject(AppDialogService);
  private groupLinkService = inject(GroupLinkService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  protected readonly authStore = inject(AuthStore);
  private toastr = inject(ToastrService);
  configurationService = inject(ConfigurationService);
  private privateSellingService = inject(PrivateSellingService);

  public groupLinksList: GroupLink[];
  public clientData: Client;
  public totalItemsInDb: number;
  public itemsPerPage: number;
  public currentPage: number;
  public refreshing: boolean;
  public subscriptions: Array<Subscription>;
  public refreshPages: boolean = false;
  public columns: ColumnDef<GroupLink>[] = [];
  @ViewChild('pagination') pagination: any;

  constructor() {
    this.groupLinksList = [];
    this.subscriptions = [];
    this.refreshing = false;
    this.totalItemsInDb = 0;
    this.itemsPerPage = 0;
    this.currentPage = this.activatedRoute.snapshot.queryParams['page'] || 1;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  ngOnInit(): void {
    this.columns = buildGroupLinkColumns({
      getUrlDisplay: groupLink => this.getFullUrl(groupLink.slug).substring(7),
      onCopyUrl: groupLink => this.copyToClipboard(groupLink.slug),
      onToggleActive: groupLink => this.toggleActive(groupLink),
      onEdit: groupLink => this.openModalEdit(groupLink),
      onDelete: groupLink => this.openModalDelete(groupLink.id),
      canEdit: this.authStore.canEdit(),
    });

    const resolved: ResolverData = this.activatedRoute.snapshot.data['api'];
    if (resolved) {
      if (resolved.groupLinksList) {
        this.groupLinksList = resolved.groupLinksList.data;
        this.totalItemsInDb = resolved.groupLinksList.metadata.total;
        this.itemsPerPage = resolved.groupLinksList.metadata.perPage;
      }
    }

    this.privateSellingService.getClientWithWhitelist(null).subscribe(res => {
      this.clientData = res.data;
      if (!this.privateSellingService.initialWhitelist().length) {
        const whitelistCopy = [
          ...(this.clientData.privateSellingWhitelist || []),
        ];
        this.privateSellingService.initialWhitelist.set(whitelistCopy);
      }
    });

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      const resolved: ResolverData = this.activatedRoute.snapshot.data['api'];
      if (!resolved || !resolved.groupLinksList) return;

      if (
        !params['page'] ||
        params['page'] > resolved.groupLinksList.metadata.lastPage
      ) {
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: { page: 1 },
        });
      }

      if (params['page']) {
        this.currentPage = params['page'];
        this.groupLinksList = resolved.groupLinksList.data;
        this.totalItemsInDb = resolved.groupLinksList.metadata.total;
        this.itemsPerPage = resolved.groupLinksList.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
      }
    });
    this.subscriptions.push(queryparams$);
  }

  public openModalDelete(id) {
    const { closed$ } = this.dialogService.open(DeleteComponent, {
      size: 'lg',
      inputs: { id },
    });
    this.refreshListOnClose(closed$, 1);
  }

  public openModalEdit(item: GroupLink) {
    const { closed$ } = this.dialogService.open(EditComponent, {
      size: 'xl',
      inputs: { item: JSON.parse(JSON.stringify(item)) },
    });
    this.refreshListOnClose(closed$, this.currentPage);
  }

  public openModalCreate() {
    const { closed$ } = this.dialogService.open(CreateComponent, {
      size: 'xl',
      inputs: { clientData: JSON.parse(JSON.stringify(this.clientData)) },
    });
    this.refreshListOnClose(closed$, 1);
  }

  private refreshListOnClose(closed$: Observable<unknown>, page?) {
    const onClose$ = closed$.subscribe(() => {
      this.refreshing = true;
      this.getall(page);
    });
    this.subscriptions.push(onClose$);
  }

  public toggleActive(item: GroupLink) {
    const wasActive = item.isActive;
    const updated: GroupLink = { ...item, isActive: !item.isActive };
    this.groupLinksList = this.groupLinksList.map(groupLink =>
      groupLink.id === item.id ? updated : groupLink
    );
    const update$ = this.groupLinkService
      .update$(item.id, { isActive: updated.isActive })
      .subscribe({
        next: res => {
          this.toastr.success(
            `Group Link ${updated.isActive ? 'activated' : 'deactivated'}`
          );
        },
        error: err => {
          console.log('Error toggling isActive:', err);
          this.groupLinksList = this.groupLinksList.map(groupLink =>
            groupLink.id === item.id
              ? { ...groupLink, isActive: wasActive }
              : groupLink
          );
          this.toastr.error(
            `Unable to ${wasActive ? 'activate' : 'deactivate'} Group Link`
          );
        },
      });
    this.subscriptions.push(update$);
  }

  private getall(page?) {
    if (!page) page = this.currentPage;

    const all$ = this.groupLinkService.getAll$(page).subscribe({
      next: (res: any) => {
        this.refreshing = false;
        this.groupLinksList = res.data;
        this.totalItemsInDb = res.metadata.total;
        this.itemsPerPage = res.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(page, this.totalItemsInDb, this.itemsPerPage);
        }
        if (page > res.metadata.lastPage) {
          this.currentPage = res.metadata.lastPage;
        } else {
          this.currentPage = page;
        }
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { page: this.currentPage },
          queryParamsHandling: 'merge',
        });
      },
      error: err => {
        console.log(err);
      },
    });
    this.subscriptions.push(all$);
  }

  public pageChanged($event) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: $event.page },
      queryParamsHandling: 'merge',
    });
  }

  public onPrivateSellingClick = () => {
    const { closed$, outputs } = this.dialogService.open(
      ManagePrivateSellingComponent,
      {
        size: 'lg',
        inputs: {
          whitelist: this.clientData.privateSellingWhitelist ?? [],
          isPrivateSellingEnabled: this.clientData.isPrivateSellingEnabled,
          clientId: this.clientData.id,
          groupLinkId: null,
        },
      }
    );

    outputs?.modifiedWhitelist.subscribe(value => {
      this.clientData.privateSellingWhitelist = [
        ...value,
      ] as PrivateSellingWhitelistItem[];
    });

    closed$.subscribe(() => {
      this.privateSellingService
        .getClientWithWhitelist(null, true)
        .subscribe(res => {
          this.clientData = res.data;
        });
    });
  };

  public getFullUrl(slug: string): string {
    return this.configurationService.getPortalBaseUrl() + '/' + slug;
  }

  public copyToClipboard(slug: string): void {
    const url = this.getFullUrl(slug);

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.toastr.success(`URL copied to clipboard`);
        })
        .catch(err => {
          console.error('Clipboard error:', err);
          this.fallbackCopyToClipboard(url);
        });
    } else {
      this.fallbackCopyToClipboard(url);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.toastr.success(`URL copied to clipboard`);
      } else {
        this.toastr.error('Unable to copy to clipboard');
      }
    } catch (err) {
      this.toastr.error('Unable to copy to clipboard');
    }

    document.body.removeChild(textArea);
  }
}
